import {
  buildPlanningDayCell,
  getDateExceptionPlagesForYmd,
  getPresenceLabelForDay,
} from './equipePlanningUtils';

export const DISPLAY_KIND = {
  HOURS: 'HOURS',
  REST: 'REST',
  UNPLANNED: 'UNPLANNED',
  ABSENT: 'ABSENT',
  LEAVE: 'LEAVE',
  UNAVAILABLE: 'UNAVAILABLE',
  CUSTOM_HOURS: 'CUSTOM_HOURS',
};

function overlapsYmd(absence, ymd) {
  if (!absence?.dateDebut) return false;
  const start = new Date(absence.dateDebut);
  const end = new Date(absence.dateFin || absence.dateDebut);
  const dayStart = new Date(`${ymd}T00:00:00`);
  const dayEnd = new Date(`${ymd}T23:59:59`);
  return start <= dayEnd && end >= dayStart;
}

function isFullDayAbsence(absence, ymd) {
  if (!overlapsYmd(absence, ymd)) return false;
  const timeFmt = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });
  const start = timeFmt.format(new Date(absence.dateDebut));
  const end = timeFmt.format(new Date(absence.dateFin));
  return start === '00:00' && (end === '23:59' || end === '00:00');
}

function formatPlagesLabel(plages) {
  if (!plages?.length) return null;
  const fmt = (hhmm) => {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':');
    const hour = String(Number(h));
    return m && m !== '00' ? `${hour}h${m}` : `${hour}h`;
  };
  return plages.map((p) => `${fmt(p.heureDebut)}–${fmt(p.heureFin)}`).join(' · ');
}

function cellToHoursLabel(cell) {
  if (!cell) return null;
  if (cell.type === 'split' || cell.type === 'exception') {
    if (cell.lines?.length) return cell.lines.join(' · ');
  }
  return cell.label || null;
}

function isLeaveAbsence(absence) {
  const type = String(absence?.type || '').toUpperCase();
  const motif = String(absence?.motif || '').toLowerCase();
  return type === 'CONGE' || motif.includes('congé') || motif.includes('conge');
}

/**
 * État métier d’affichage pour une cellule jour (Mois / détail Gestion).
 */
export function getDayPlanningDisplayState({
  horairesConfig,
  absences = [],
  jourSemaine,
  ymd,
  dateExceptionsByDate = null,
}) {
  const overridePlages = getDateExceptionPlagesForYmd(dateExceptionsByDate, ymd);
  const hasDateOverride = overridePlages !== undefined;
  const baseLabel =
    horairesConfig && jourSemaine != null
      ? getPresenceLabelForDay(horairesConfig, jourSemaine)
      : null;

  const dayAbsences = (absences || []).filter((a) => overlapsYmd(a, ymd));
  const fullDayAbsence = dayAbsences.find((a) => isFullDayAbsence(a, ymd));

  if (fullDayAbsence) {
    const kind = isLeaveAbsence(fullDayAbsence) ? DISPLAY_KIND.LEAVE : DISPLAY_KIND.ABSENT;
    return {
      kind,
      label: kind === DISPLAY_KIND.LEAVE ? 'Congé' : 'Absent',
      plannedLabel: kind === DISPLAY_KIND.LEAVE ? 'Congé' : 'Absent',
      baseLabel,
      hasDateOverride,
      showExceptionBadge: false,
      conformsToBase: false,
      dayAbsences,
      cell: { type: 'absence' },
    };
  }

  if (hasDateOverride && overridePlages.length === 0) {
    return {
      kind: DISPLAY_KIND.REST,
      label: 'Repos',
      plannedLabel: 'Repos',
      baseLabel,
      hasDateOverride: true,
      showExceptionBadge: true,
      restBadge: true,
      conformsToBase: false,
      dayAbsences,
      cell: { type: 'off' },
    };
  }

  const cell = horairesConfig
    ? buildPlanningDayCell(
        horairesConfig,
        absences,
        jourSemaine,
        ymd,
        dateExceptionsByDate,
      )
    : { type: 'off' };

  const plannedFromCell = cellToHoursLabel(cell);

  if (!hasDateOverride && (cell.type === 'off' || !plannedFromCell)) {
    return {
      kind: DISPLAY_KIND.UNPLANNED,
      label: 'Non planifié',
      plannedLabel: 'Non planifié',
      baseLabel: baseLabel || null,
      hasDateOverride: false,
      showExceptionBadge: false,
      conformsToBase: false,
      dayAbsences,
      cell,
    };
  }

  if (cell.type === 'partial') {
    return {
      kind: DISPLAY_KIND.UNAVAILABLE,
      label: 'Indispo',
      plannedLabel: 'Indispo',
      baseLabel,
      hasDateOverride,
      showExceptionBadge: false,
      conformsToBase: false,
      dayAbsences,
      cell,
    };
  }

  if (hasDateOverride && overridePlages.length > 0) {
    const plannedLabel = formatPlagesLabel(overridePlages) || plannedFromCell;
    return {
      kind: DISPLAY_KIND.CUSTOM_HOURS,
      label: plannedLabel,
      plannedLabel,
      baseLabel,
      hasDateOverride: true,
      showExceptionBadge: true,
      conformsToBase: false,
      dayAbsences,
      cell,
    };
  }

  const plannedLabel = plannedFromCell || 'Non planifié';
  const conformsToBase =
    Boolean(baseLabel) &&
    Boolean(plannedLabel) &&
    !hasDateOverride &&
    plannedLabel.replace(/\s/g, '') === baseLabel.replace(/\s/g, '');

  return {
    kind: DISPLAY_KIND.HOURS,
    label: plannedLabel,
    plannedLabel,
    baseLabel,
    hasDateOverride: false,
    showExceptionBadge: false,
    conformsToBase,
    dayAbsences,
    cell,
  };
}
